import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AppliedRoundOperation, OperationHistory, RoundOperation } from "../../domain/manualBoard/operations";
import { applyInverse, applyRoundOperation } from "../../domain/manualBoard/engine";
import type { AssignmentSegment, NormalizedRoundSchedule, RoundAssignment, RoundName } from "../../domain/round/types";
import type { TransportPlan, TransportPreviewResult } from "../../domain/transport/types";
import { ApiError, api, type TournamentSummary } from "../../lib/api";
import { roundApi, type ScheduleValidation } from "../../lib/roundApi";
import { initialRoundName, initialSeason, initialTournamentId, persistRoundSelection } from "../../lib/roundSelection";
import { ManualBoardPersonPicker, type ManualBoardAddTarget } from "./ManualBoardPersonPicker";
import { ManualBoardTable, type ManualBoardTableCallbacks } from "./ManualBoardTable";
import { buildManualBoardPeople, buildUnassignedPool, compareManualBoardSchedules } from "./manual-board-model";
import { resolveManualConflict } from "./manualConflictApi";
import "./manual-board.css";
import "./manual-board-operations.css";

const ROUNDS: RoundName[] = ["R1", "R2", "R3", "R4"];

function _holeAfter(tee: number, offset: number): number {
  return ((tee - 1 + offset) % 18) + 1;
}

function _inferFirstHalfHoles(group: { startMinutes: number; finish9Minutes?: number | null; finish18Minutes?: number | null }): number {
  const start = group.startMinutes;
  const total = (group.finish18Minutes ?? start) - start;
  const toFirst = (group.finish9Minutes ?? start) - start;
  if (total <= 0) return 9;
  return Math.min(17, Math.max(1, Math.round((toFirst / total) * 18)));
}

function _holeRangeForSegment(tee: number, firstHalfHoles: number, segment: string): string {
  let startOffset = 0;
  let endOffset = 17;
  if (segment === "holes1to9" || segment === "extra1to9") { endOffset = firstHalfHoles - 1; }
  else if (segment === "holes10to18" || segment === "extra10to18") { startOffset = firstHalfHoles; }
  const start = _holeAfter(tee, startOffset);
  const end = _holeAfter(tee, endOffset);
  if (end >= start) return `${start}-${end}`;
  if (start === 18) return `18, 1-${end}`;
  return `${start}-18, 1-${end}`;
}

function chooseTournament(rows: TournamentSummary[]): string {
  if (!rows.length) return "";
  const today = new Date().toISOString().slice(0, 10);
  const started = rows.filter((row) => row.startDate && row.startDate <= today);
  return (started.at(-1) || rows[0]).id;
}

function operationLabel(operation: RoundOperation) {
  if (operation.type === "move-assignment") return "Przeniesienie pozycji";
  if (operation.type === "swap-assignment") return "Zamiana dwóch pozycji";
  if (operation.type === "full-plan-swap") return "Pełna zamiana planu";
  if (operation.type === "assign-from-pool") return "Dodanie scorera";
  if (operation.type === "remove-assignment") return "Usunięcie z pozycji";
  return operation.locked ? "Zablokowanie pozycji" : "Odblokowanie pozycji";
}

function deltaLabel(value: number, suffix = "") {
  if (!value) return "bez zmian";
  return `${value > 0 ? "+" : ""}${value}${suffix}`;
}

export function ManualBoardOperationsPage() {
  const queryClient = useQueryClient();
  const [season, setSeason] = useState(initialSeason);
  const [tournamentId, setTournamentId] = useState(initialTournamentId);
  const [round, setRound] = useState<RoundName>(initialRoundName);
  const [schedule, setSchedule] = useState<NormalizedRoundSchedule | null>(null);
  const [transport, setTransport] = useState<TransportPlan | null>(null);
  const [previewResult, setPreviewResult] = useState<TransportPreviewResult | null>(null);
  const [transportChanged, setTransportChanged] = useState(false);
  const [workspaceRevision, setWorkspaceRevision] = useState(0);
  const [transportStale, setTransportStale] = useState(false);
  const [history, setHistory] = useState<OperationHistory>({ past: [], future: [] });
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const [selectedQuickPersonId, setSelectedQuickPersonId] = useState("");
  const [addTarget, setAddTarget] = useState<ManualBoardAddTarget | null>(null);
  const [swapA, setSwapA] = useState("");
  const [swapB, setSwapB] = useState("");
  const [notice, setNotice] = useState("");
  const [conflict, setConflict] = useState(false);
  const [scheduleValidation, setScheduleValidation] = useState<ScheduleValidation | null>(null);
  const [validationPending, setValidationPending] = useState(false);
  const autoRoundKeyRef = useRef("");

  const me = useQuery({ queryKey: ["auth", "me"], queryFn: api.me, staleTime: 30_000 });
  const tournaments = useQuery({ queryKey: ["tournaments", season], queryFn: () => api.tournaments(season) });
  const roundWorkspaces = useQueries({
    queries: ROUNDS.map((roundName) => ({
      queryKey: ["round-workspace", tournamentId, roundName],
      queryFn: () => api.roundWorkspace(tournamentId, roundName),
      enabled: Boolean(tournamentId),
      staleTime: 10_000,
    })),
  });
  const roundStateSignature = roundWorkspaces.map((state) => state.isPending ? "pending" : state.isError ? "error" : state.data?.confirmation.confirmed ? "confirmed" : "open").join("|");

  useEffect(() => {
    const rows = tournaments.data?.tournaments || [];
    if (rows.length && (!tournamentId || !rows.some((row) => row.id === tournamentId))) setTournamentId(chooseTournament(rows));
  }, [tournaments.data, tournamentId]);

  useEffect(() => {
    if (!tournamentId) return;
    const key = `${season}:${tournamentId}`;
    if (autoRoundKeyRef.current === key) return;
    if (roundWorkspaces.some((state) => state.isPending || state.isError || !state.data)) return;
    autoRoundKeyRef.current = key;
    const nextIndex = roundWorkspaces.findIndex((state) => !state.data?.confirmation.confirmed);
    const nextRound: RoundName = nextIndex >= 0 ? ROUNDS[nextIndex] : "R4";
    if (round !== nextRound) setRound(nextRound);
  }, [tournamentId, season, round, roundStateSignature]);

  useEffect(() => {
    if (tournamentId) persistRoundSelection(tournamentId, round, season);
  }, [tournamentId, round, season]);

  const workspace = useQuery({
    queryKey: ["round-workspace", tournamentId, round],
    queryFn: () => api.roundWorkspace(tournamentId, round),
    enabled: Boolean(tournamentId),
  });

  const resetFromWorkspace = () => {
    const data = workspace.data;
    if (!data) return;
    setSchedule(data.schedule);
    setTransport(data.transport);
    setPreviewResult(null);
    setTransportChanged(false);
    setWorkspaceRevision(data.workspaceRevision);
    setTransportStale(data.transportStale);
    setHistory({ past: [], future: [] });
    setSelectedAssignmentId("");
    setSelectedQuickPersonId("");
    setAddTarget(null);
    setSwapA("");
    setSwapB("");
    setNotice("");
    setConflict(false);
    const serverValidation = (data as unknown as { scheduleValidation?: ScheduleValidation }).scheduleValidation;
    setScheduleValidation(serverValidation || null);
  };

  useEffect(() => resetFromWorkspace(), [workspace.data]);

  useEffect(() => {
    if (!schedule || !tournamentId) {
      setScheduleValidation(null);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setValidationPending(true);
      void roundApi.validateSchedule(tournamentId, round, schedule)
        .then((result) => { if (active) setScheduleValidation(result); })
        .catch(() => { if (active) setScheduleValidation(null); })
        .finally(() => { if (active) setValidationPending(false); });
    }, 220);
    return () => { active = false; window.clearTimeout(timer); };
  }, [schedule, tournamentId, round]);

  const holeRangesByPerson = useMemo(() => {
    const groupsById = new Map((schedule?.groups || []).map((g) => [g.id, g]));
    const ranges = new Map<string, string[]>();
    for (const a of schedule?.assignments || []) {
      const group = groupsById.get(a.groupId);
      if (!group || group.tee == null) continue;
      const label = _holeRangeForSegment(Number(group.tee), _inferFirstHalfHoles(group), a.segment);
      const list = ranges.get(a.personId) || [];
      list.push(label);
      ranges.set(a.personId, list);
    }
    return ranges;
  }, [schedule]);
  const holesSummary = useMemo(() => {
    const groups = new Map<number, { personId: string; displayName: string }[]>();
    for (const person of schedule?.people || []) {
      const list = groups.get(person.holes) || [];
      list.push({ personId: person.personId, displayName: person.displayName });
      groups.set(person.holes, list);
    }
    return [...groups.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([holes, list]) => ({ holes, list: list.sort((a, b) => a.displayName.localeCompare(b.displayName, "pl")) }));
  }, [schedule]);
  const people = useMemo(() => buildManualBoardPeople(workspace.data?.staff || [], schedule), [workspace.data?.staff, schedule]);
  const peopleById = useMemo(() => new Map(people.map((person) => [person.personId, person.displayName])), [people]);
  const poolGroups = useMemo(() => buildUnassignedPool(people, schedule), [people, schedule]);
  const poolCount = poolGroups.reduce((sum, group) => sum + group.people.length, 0);
  const selectedQuickPerson = people.find((person) => person.personId === selectedQuickPersonId);
  const selectedTournament = tournaments.data?.tournaments.find((item) => item.id === tournamentId);
  const readOnly = Boolean(workspace.data?.selectedSource === "confirmed" && !workspace.data.availability.working);
  const dirty = history.past.length > 0 || transportChanged;
  const validationBlockers = scheduleValidation?.blockers || [];
  const validationWarnings = scheduleValidation?.warnings || [];
  const scheduleIsValid = Boolean(schedule && scheduleValidation?.valid && !validationPending);
  const comparison = useMemo(() => compareManualBoardSchedules(workspace.data?.schedule, schedule, peopleById), [workspace.data?.schedule, schedule, peopleById]);

  const invalidateTransport = () => {
    setTransportStale(true);
    setPreviewResult(null);
  };

  const execute = (operation: RoundOperation) => {
    if (!schedule || readOnly) return;
    try {
      const result = applyRoundOperation(schedule, operation);
      const applied: AppliedRoundOperation = {
        id: crypto.randomUUID(), operation, inverse: result.inverse,
        createdAt: new Date().toISOString(), actorId: me.data?.user.id || "local-operator",
        affectsTransport: result.affectsTransport,
      };
      setSchedule(result.schedule);
      setHistory((current) => ({ past: [...current.past, applied].slice(-150), future: [] }));
      if (result.affectsTransport) invalidateTransport();
      setConflict(false);
      setNotice("");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Nie udało się wykonać operacji.");
    }
  };

  const undo = () => {
    const applied = history.past.at(-1);
    if (!applied || !schedule || readOnly) return;
    try {
      setSchedule(applyInverse(schedule, applied.inverse));
      setHistory({ past: history.past.slice(0, -1), future: [applied, ...history.future] });
      if (applied.affectsTransport) invalidateTransport();
      setSelectedAssignmentId(""); setNotice("");
    } catch (error) { setNotice(error instanceof Error ? error.message : "Nie udało się cofnąć operacji."); }
  };

  const redo = () => {
    const applied = history.future[0];
    if (!applied || !schedule || readOnly) return;
    try {
      const result = applyRoundOperation(schedule, applied.operation);
      setSchedule(result.schedule);
      setHistory({ past: [...history.past, applied], future: history.future.slice(1) });
      if (applied.affectsTransport) invalidateTransport();
      setSelectedAssignmentId(""); setNotice("");
    } catch (error) { setNotice(error instanceof Error ? error.message : "Nie udało się ponowić operacji."); }
  };

  const transportPreview = useMutation({
    mutationFn: () => {
      if (!schedule) throw new Error("Brak rozpiski do przeliczenia.");
      if (!scheduleIsValid) throw new Error("Najpierw popraw błędy blokujące w rozpisce.");
      return api.transportPreview(tournamentId, round, { expectedRevision: workspaceRevision, schedule, currentTransport: transport });
    },
    onSuccess: (result) => {
      setPreviewResult(result);
      const blockers = result.plan.issues.filter((issue) => issue.severity === "blocker").length;
      setNotice(blockers ? `Preview transportu ma ${blockers} blockerów.` : "Preview transportu gotowy do porównania.");
    },
  });

  const acceptPreview = () => {
    if (!previewResult || previewResult.plan.issues.some((issue) => issue.severity === "blocker")) return;
    setTransport(previewResult.plan); setTransportStale(false); setTransportChanged(true); setPreviewResult(null);
    setNotice("Nowy transport został zastosowany lokalnie. Zapis utrwali rozpiskę i transport razem.");
  };

  const save = useMutation({
    mutationFn: () => {
      if (!schedule) throw new Error("Brak workspace do zapisania.");
      if (!scheduleIsValid) throw new Error("Nie można zapisać: rozpiska ma błędy blokujące.");
      return api.saveRoundWorkspace(tournamentId, round, { expectedRevision: workspaceRevision, schedule, transport, transportStale });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["round-workspace", tournamentId, round], result);
      setNotice(`Zapisano workspace r${result.workspaceRevision}.`); setConflict(false);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) { setConflict(true); setNotice(""); }
    },
  });

  const resolveConflict = useMutation({
    mutationFn: () => {
      if (!schedule) throw new Error("Brak lokalnej rozpiski do zachowania.");
      if (!scheduleIsValid) throw new Error("Najpierw popraw lokalne błędy blokujące.");
      return resolveManualConflict(tournamentId, round, { baseRevision: workspaceRevision, schedule, transport, transportStale });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["round-workspace", tournamentId, round], result);
      setSchedule(result.schedule); setTransport(result.transport); setWorkspaceRevision(result.workspaceRevision); setTransportStale(result.transportStale);
      setHistory({ past: [], future: [] }); setTransportChanged(false); setConflict(false);
      setNotice(`Konflikt rozwiązany: zachowano Twoją poprawną wersję jako workspace r${result.workspaceRevision}.`);
    },
  });

  const reloadAfterConflict = async () => {
    const result = await workspace.refetch();
    if (result.data) {
      setSchedule(result.data.schedule); setTransport(result.data.transport); setWorkspaceRevision(result.data.workspaceRevision);
      setTransportStale(result.data.transportStale); setHistory({ past: [], future: [] }); setSelectedAssignmentId("");
      setSelectedQuickPersonId(""); setConflict(false); setNotice("Wczytano najnowszą wersję workspace.");
    }
  };

  const clickAssignment = (assignmentId: string) => {
    setSelectedQuickPersonId("");
    if (!selectedAssignmentId) { setSelectedAssignmentId(assignmentId); return; }
    if (selectedAssignmentId === assignmentId) { setSelectedAssignmentId(""); return; }
    execute({ type: "swap-assignment", firstAssignmentId: selectedAssignmentId, secondAssignmentId: assignmentId });
    setSelectedAssignmentId("");
  };

  const tableCallbacks: ManualBoardTableCallbacks = {
    readOnly, selectedAssignmentId, selectedQuickPersonId, selectedQuickPersonName: selectedQuickPerson?.displayName || "", peopleById,
    onAssignmentClick: clickAssignment,
    onMoveSelected: (groupId, segment) => { if (!selectedAssignmentId) return; execute({ type: "move-assignment", assignmentId: selectedAssignmentId, toGroupId: groupId, toSegment: segment }); setSelectedAssignmentId(""); },
    onAssignQuickPerson: (groupId, segment) => { if (!selectedQuickPersonId) return; execute({ type: "assign-from-pool", personId: selectedQuickPersonId, groupId, segment }); },
    onOpenPersonPicker: (groupId, segment) => { const group = schedule?.groups.find((item) => item.id === groupId); if (group) setAddTarget({ groupId, groupNumber: group.number, segment }); },
    onToggleLock: (assignment: RoundAssignment) => execute({ type: "set-lock", assignmentId: assignment.id, locked: !assignment.locked }),
    onRemove: (assignment: RoundAssignment) => execute({ type: "remove-assignment", assignmentId: assignment.id }),
  };

  const fullSwapCandidatesA = (schedule?.people || []).filter((person) => person.assignmentIds.length > 0);
  const fullSwapCandidatesB = (schedule?.people || []).filter((person) => person.assignmentIds.length > 0);
  const loadError = workspace.error || tournaments.error;
  const errorText = loadError instanceof ApiError ? loadError.message : loadError instanceof Error ? loadError.message : "";
  const saveError = save.error instanceof ApiError && save.error.status !== 409 ? save.error.message : save.error instanceof Error ? save.error.message : "";
  const previewError = transportPreview.error instanceof ApiError ? transportPreview.error.message : transportPreview.error instanceof Error ? transportPreview.error.message : "";
  const conflictError = resolveConflict.error instanceof Error ? resolveConflict.error.message : "";
  const previewBlockers = previewResult?.plan.issues.filter((issue) => issue.severity === "blocker") || [];

  return (
    <section className="manual-page mbo-page">
      <article className="panel manual-context mbo-context">
        <div><span className="eyebrow">RUNDA · EDYCJA</span><h2>{selectedTournament?.name || "Wybierz turniej"}</h2><p>Ręczna korekta z pełną swobodą układania, ale z twardym walidatorem operacyjnych niemożliwości.</p></div>
        <div className="manual-context-controls">
          <label><span>Sezon</span><select value={season} onChange={(event) => setSeason(Number(event.target.value))}>{[2026, 2025, 2024].map((value) => <option key={value}>{value}</option>)}</select></label>
          <label className="manual-tournament"><span>Turniej</span><select value={tournamentId} onChange={(event) => setTournamentId(event.target.value)}>{(tournaments.data?.tournaments || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <div className="manual-rounds">{ROUNDS.map((value, index) => {
            const confirmed = Boolean(roundWorkspaces[index]?.data?.confirmation.confirmed);
            return <button key={value} type="button" className={`${round === value ? "active " : ""}${confirmed ? "confirmed" : ""}`.trim()} onClick={() => setRound(value)}><span>{value}</span>{confirmed ? <small title="Runda zatwierdzona">✓</small> : null}</button>;
          })}</div>
        </div>
      </article>

      {readOnly ? <div className="manual-notice confirmed">Runda jest zatwierdzona. Ten workspace jest tylko do odczytu.</div> : null}
      {conflict ? <article className="mbo-conflict"><div><strong>Nowsza wersja workspace jest już na serwerze.</strong><span>Twoje lokalne zmiany nadal są zachowane w tej karcie. Możesz wczytać serwer albo jawnie zachować swoją wersję — przed zapisem przejdzie ponownie pełny walidator.</span></div><div><button type="button" className="secondary-button" disabled={resolveConflict.isPending} onClick={() => void reloadAfterConflict()}>Wczytaj wersję serwera</button><button type="button" className="primary-button" disabled={!scheduleIsValid || resolveConflict.isPending} onClick={() => resolveConflict.mutate()}>{resolveConflict.isPending ? "Sprawdzam i zapisuję…" : "Sprawdź i zachowaj moje zmiany"}</button></div></article> : null}
      {notice || saveError || previewError || conflictError || errorText ? <div className={`manual-notice ${saveError || previewError || conflictError || errorText ? "error" : ""}`}>{saveError || previewError || conflictError || errorText || notice}</div> : null}

      {schedule ? (
        <article className={`panel mbo-validation ${validationBlockers.length ? "blocked" : "valid"}`}>
          <header><div><span className="eyebrow">WALIDATOR ROZPISKI</span><h3>{validationPending ? "Sprawdzam…" : validationBlockers.length ? `${validationBlockers.length} błędów blokujących` : "Rozpiska operacyjnie poprawna"}</h3></div><strong>{validationBlockers.length ? "NIE ZAPISZESZ" : "✓ OK"}</strong></header>
          {validationBlockers.length ? <div className="mbo-validation-list">{validationBlockers.slice(0, 12).map((issue, index) => <button type="button" key={`${issue.code}-${issue.groupId}-${issue.personId}-${index}`}><b>BLOCKER</b><span>{issue.message}</span></button>)}</div> : <p>Możesz zapisać. Fairness, pokoje, rotacja kierowców i optymalizacja pozostają kryteriami solvera, a nie sztucznymi hard-blockerami.</p>}
          {validationWarnings.length ? <small>{validationWarnings.length} dodatkowych ostrzeżeń.</small> : null}
        </article>
      ) : null}

      <div className="mbo-kpi-grid">
        <article className="panel mbo-kpi"><span>Grupy</span><strong>{schedule?.groups.length || 0}</strong><small>{round === "R1" || round === "R2" ? "2 sesje" : "1 sesja"}</small></article>
        <article className="panel mbo-kpi"><span>Nieprzypisani</span><strong>{poolCount}</strong><small>{people.length} osób dostępnych</small></article>
        <article className="panel mbo-kpi"><span>Zmiany lokalne</span><strong>{history.past.length}</strong><small>{comparison.changedPeople.length} osób z wpływem</small></article>
        <article className={`panel mbo-kpi ${transportStale ? "warn" : "ok"}`}><span>Transport</span><strong>{transportStale ? "STALE" : "OK"}</strong><small>workspace r{workspaceRevision}</small></article>
        <article className={`panel mbo-kpi ${validationBlockers.length ? "warn" : "ok"}`}><span>Walidacja</span><strong>{validationBlockers.length}</strong><small>blockerów</small></article>
      </div>

      <article className="panel manual-toolbar mbo-toolbar">
        <div className="manual-toolbar-group">
          <button className="secondary-button" type="button" onClick={undo} disabled={readOnly || !history.past.length}>Cofnij</button>
          <button className="secondary-button" type="button" onClick={redo} disabled={readOnly || !history.future.length}>Ponów</button>
          <button className="secondary-button" type="button" disabled={!dirty} onClick={resetFromWorkspace}>Anuluj zmiany</button>
          {selectedAssignmentId ? <button className="mbo-selection-clear" type="button" onClick={() => setSelectedAssignmentId("")}>Anuluj zaznaczenie</button> : null}
          {selectedQuickPersonId ? <button className="mbo-selection-clear person" type="button" onClick={() => setSelectedQuickPersonId("")}>+ {selectedQuickPerson?.displayName} · zakończ</button> : null}
        </div>
        <div className="manual-toolbar-group right">
          <button className="secondary-button" type="button" disabled={!schedule || readOnly || transportPreview.isPending || !scheduleIsValid} onClick={() => transportPreview.mutate()}>{transportPreview.isPending ? "Przeliczam…" : "Przelicz transport"}</button>
          <button className="primary-button" type="button" disabled={readOnly || !dirty || save.isPending || !schedule || !scheduleIsValid} onClick={() => save.mutate()}>{save.isPending ? "Zapisuję…" : validationBlockers.length ? "Popraw błędy przed zapisem" : "Zapisz workspace"}</button>
        </div>
      </article>

      {previewResult ? <article className="panel mbo-preview"><header><div><span className="eyebrow">TRANSPORT PREVIEW</span><h3>Wpływ po przeliczeniu</h3></div><div><button type="button" className="secondary-button" onClick={() => setPreviewResult(null)}>Odrzuć</button><button type="button" className="primary-button" disabled={previewBlockers.length > 0} onClick={acceptPreview}>Użyj planu</button></div></header><div className="mbo-preview-grid"><div><span>Max rano</span><strong>{previewResult.plan.metrics.morningMaxWaitMinutes} min</strong></div><div><span>Max powrót</span><strong>{previewResult.plan.metrics.returnMaxWaitMinutes} min</strong></div><div><span>Zmienione kursy</span><strong>{previewResult.changedVehicleTrips.length}</strong></div><div><span>Zmiany osób</span><strong>{previewResult.personImpacts.length}</strong></div></div>{previewBlockers.map((issue) => <div className="transport-issue blocker" key={issue.id}><strong>BLOCKER</strong><span>{issue.message}</span></div>)}</article> : null}

      {schedule ? (
        <div className="mbo-workspace-grid">
          <main className="mbo-board-area"><ManualBoardTable schedule={schedule} callbacks={tableCallbacks} /></main>
          <aside className="mbo-side-area">
            <article className="panel mbo-holes-card"><header><div><span className="eyebrow">DOŁKI</span><h3>Kto ile gra</h3></div></header><div className="mbo-holes-groups">{holesSummary.map((group) => <section key={group.holes}><h4>{group.holes} dołków <small>({group.list.length})</small></h4><div className="mbo-holes-chips">{group.list.map((person) => <span key={person.personId}>{person.displayName}{holeRangesByPerson.get(person.personId)?.length ? ` (${holeRangesByPerson.get(person.personId)!.join(", ")})` : ""}</span>)}</div></section>)}{!holesSummary.length ? <p className="mbo-empty">Brak danych o dołkach.</p> : null}</div></article>
            <article className="panel mbo-pool-card"><header><div><span className="eyebrow">MIESZKANIA / HOTEL</span><h3>Nieprzypisani scorerzy</h3></div><strong>{poolCount}</strong></header><p>Kliknij osobę, aby szybko dodawać ją do pozycji. Osoba usunięta z ostatniej pozycji wraca tutaj automatycznie.</p><div className="mbo-pool-list">{poolGroups.map((group) => <section key={group.baseName}><h4>{group.baseName}</h4>{group.people.map((person) => <button type="button" disabled={readOnly} className={selectedQuickPersonId === person.personId ? "selected" : ""} key={person.personId} onClick={() => { setSelectedAssignmentId(""); setSelectedQuickPersonId((current) => current === person.personId ? "" : person.personId); }}><span>{person.displayName}</span>{person.driverEligible ? <small>K</small> : null}</button>)}</section>)}{!poolGroups.length ? <div className="mbo-empty">Brak całkowicie wolnych osób. Nadal możesz użyć „+” w tabeli i wybrać kogoś już pracującego.</div> : null}</div></article>
            <article className="panel mbo-impact-card"><header><span className="eyebrow">WPŁYW ZMIAN</span><h3>Obsada i obciążenie</h3></header><div className="mbo-impact-summary"><span><strong>{comparison.changedAssignments}</strong><small>zmienionych pozycji</small></span><span><strong>{comparison.peopleWithMoreHoles}</strong><small>więcej dołków</small></span><span><strong>{comparison.peopleWithFewerHoles}</strong><small>mniej dołków</small></span></div><div className="mbo-impact-people">{comparison.changedPeople.slice(0, 10).map((person) => <div key={person.personId} className={person.holesDelta > 0 ? "more" : person.holesDelta < 0 ? "less" : "neutral"}><strong>{person.displayName}</strong><span>dołki {person.holesBefore} → {person.holesAfter} <b>{deltaLabel(person.holesDelta)}</b></span>{person.longestBreakBefore !== person.longestBreakAfter ? <small>przerwa {person.longestBreakBefore} → {person.longestBreakAfter} min</small> : null}</div>)}{!comparison.changedPeople.length ? <p>Brak zmian względem wczytanej wersji.</p> : null}</div></article>
            <article className="panel mbo-swap-card"><header><span className="eyebrow">PEŁNA ZAMIANA PLANU</span><h3>A ↔ B</h3></header><p className="mbo-swap-help">Zamienia wszystkie pozycje dwóch osób w tej rundzie — niezależnie od tego, czy mają 9, 18, 27 czy 36 dołków.</p><label><span>Osoba A</span><select disabled={readOnly} value={swapA} onChange={(event) => setSwapA(event.target.value)}><option value="">Wybierz…</option>{fullSwapCandidatesA.map((person) => <option key={person.personId} value={person.personId}>{person.displayName} · {person.holes} dołków</option>)}</select></label><label><span>Osoba B</span><select disabled={readOnly} value={swapB} onChange={(event) => setSwapB(event.target.value)}><option value="">Wybierz…</option>{fullSwapCandidatesB.map((person) => <option key={person.personId} value={person.personId}>{person.displayName} · {person.holes} dołków</option>)}</select></label><button className="secondary-button" type="button" disabled={readOnly || !swapA || !swapB || swapA === swapB} onClick={() => { execute({ type: "full-plan-swap", personAId: swapA, personBId: swapB }); setSwapA(""); setSwapB(""); }}>Zamień całe plany A ↔ B</button></article>
            <article className="panel mbo-history-card"><header><span className="eyebrow">HISTORIA LOKALNA</span><h3>Ostatnie operacje</h3></header><div>{history.past.slice(-10).reverse().map((entry) => <span key={entry.id}><strong>{operationLabel(entry.operation)}</strong><small>{entry.affectsTransport ? "wpływa na transport" : "bez transportu"}</small></span>)}</div>{!history.past.length ? <p>Brak lokalnych zmian.</p> : null}</article>
          </aside>
        </div>
      ) : workspace.isPending ? <article className="panel round-loading"><div className="round-loading-bar" /><span>Wczytuję Manual Board…</span></article> : <article className="panel round-empty">Brak rozpiski dla tej rundy.</article>}

      {addTarget && schedule ? <ManualBoardPersonPicker target={addTarget} people={people} schedule={schedule} onClose={() => setAddTarget(null)} onPick={(personId) => { execute({ type: "assign-from-pool", personId, groupId: addTarget.groupId, segment: addTarget.segment as AssignmentSegment }); setAddTarget(null); }} /> : null}
    </section>
  );
}